import { useState } from 'react';
import { version as APP_VERSION } from '../../../package.json';
import { goBack, resetNav, type SettingsPage } from '../../app/navigation';
import { V2_SCHEMA_VERSION } from '../../domain/inspection';
import { setAppLanguagePreference, useT, type MessageKey } from '../../i18n/i18n';
import { APP_LANGUAGE_PREFERENCE_ORDER, type AppLanguagePreference } from '../../i18n/locales';
import { V2_DATABASE_NAME, V2_NAMESPACE_PREFIX } from '../../persistence/persistenceContract';
import { readInspectionDataResetLedger } from '../../persistence/v2Database';
import { deleteAccountAction, type DeletionOutcome } from '../../state/accountDeletion';
import { exportAllReportsAction } from '../../state/exportAllReports';
import { useStore } from '../../state/createStore';
import { evidenceStore } from '../../state/evidenceStore';
import { findingStore } from '../../state/findingStore';
import { inspectionStore } from '../../state/inspectionStore';
import { localeStore } from '../../i18n/i18n';
import { AGREEMENT_NY_MANDATORY_CLAUSES } from '../../domain/agreementText';
import { scopeTextFor } from '../../report/agreementHtml';
import { reportStore } from '../../state/reportStore';
import { storageStatusFor, syncStatusStore } from '../../state/syncStatus';
import { sendPasswordReset, sessionStore, signOutAction } from '../../state/sessionStore';
import { boundLicenceFields, currentInspectorProfile, readInspectorProfile, writeInspectorProfile } from '../../state/preferences';
import { InspectorPhotoBlock } from './InspectorAvatar';
import { Banner, Button, Card, Chip, ConfirmSheet, Field, InlineMessage, SectionHeader, StatusChip, TextInput, TopBar } from '../../ui/components';
import { formatDate, useOnlineStatus } from '../../ui/hooks';
import { LicenceBinding, useBoundLicense } from './LicenceBinding';
import { accountStore } from '../../state/accountStore';
import { PlanPage } from './PlanPage';
import { ReportBrandingPage } from './ReportBrandingPage';
import { CommentsPage } from './CommentsPage';
import { settingsPageTitle } from './SettingsScreen';
import { webVersionUi } from '../../app/webVersion';

// The complete privacy policy and terms (zh + en) — the URLs registered with App Store Connect / Play Console.
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from './legalLinks';

// The app identity is a constant, not copy (same bundle id as the App Store record — Owner ruling 2026-09-01).
const BUNDLE_ID = 'com.nezha2capital.yanwushi';
const BUNDLE_ID_LABEL = 'Bundle ID';

// Settings sub-pages (task book §20). Each page states facts about THIS build and this device;
// none of them invents a feature the build does not have (no cloud sync engine). Purchases are real
// from 2026-09-11 and live on their own page (./PlanPage.tsx).
export function SettingsPageScreen(props: { page: SettingsPage }) {
  const { t } = useT();
  return (
    <div className="screen" data-testid={`settings-page-${props.page}`}>
      <TopBar title={settingsPageTitle(t, props.page)} onBack={goBack} />
      <div className="screen-body">
        <PageBody page={props.page} />
      </div>
    </div>
  );
}

function PageBody(props: { page: SettingsPage }) {
  switch (props.page) {
    case 'language':
      return <LanguagePage />;
    case 'profile':
      return <ProfilePage />;
    case 'report':
      return <ReportBrandingPage />;
    case 'comments':
      return <CommentsPage />;
    case 'account':
      return <AccountPage />;
    case 'sync':
      return <SyncPage />;
    case 'photos':
      return <PhotosPage />;
    case 'plan':
      return <PlanPage />;
    case 'help':
      return <HelpPage />;
    case 'privacy':
      return <PrivacyPage />;
    case 'terms':
      return <TermsPage />;
    case 'about':
      return <AboutPage />;
  }
}

const PREFERENCE_KEYS: Record<AppLanguagePreference, MessageKey> = {
  system: 'settings.languageSystem',
  'zh-CN': 'language.chinese',
  'en-US': 'language.english'
};

function LanguagePage() {
  const { t } = useT();
  const preference = useStore(localeStore, (s) => s.preference);
  const locale = useStore(localeStore, (s) => s.locale);
  return (
    <Card testId="app-language-card">
      <SectionHeader title={t('shell.appLanguage')} />
      <div className="chips" data-testid="language-preference">
        {APP_LANGUAGE_PREFERENCE_ORDER.map((option) => (
          <Chip key={option} label={t(PREFERENCE_KEYS[option])} selected={preference === option} onClick={() => setAppLanguagePreference(option)} testId={`language-${option}`} />
        ))}
      </div>
      <p className="card-note" style={{ marginTop: 12 }}>
        {preference === 'system' ? t('settings.languageSystemBody', { locale: t(locale === 'zh-CN' ? 'language.chinese' : 'language.english') }) : t('language.appLanguageBody')}
      </p>
      <p className="card-note" style={{ marginTop: 4 }}>
        {t('language.reportLanguageDefault')}
      </p>
      {/* Device walkthrough 2026-10-27 E1: AI text is written in the App Language of the moment it ran and is
          stored as written, so after a switch the earlier results stay in the old language. Said once, here. */}
      <p className="card-note" style={{ marginTop: 4 }} data-testid="language-ai-note">
        {t('language.aiResultsNote')}
      </p>
    </Card>
  );
}

const DELETION_KEYS: Record<DeletionOutcome['kind'], MessageKey> = {
  committed: 'settings.deleteDone',
  unauthenticated: 'settings.deleteUnauthenticated',
  unavailable: 'settings.deleteUnavailable',
  denied: 'settings.deleteDenied',
  unknown: 'settings.deleteUnknown'
};

// Inspector Profile. The display name and photo are the inspector's own; the licence is the one the
// server bound to the account after New York's list confirmed it (Owner decision list 2026-10-19 item 50,
// src/features/settings/LicenceBinding.tsx) — snapshotted onto every new inspection at creation, so a
// delivered report keeps the licence it was produced under.
function ProfilePage() {
  const { t } = useT();
  const [stored, setStored] = useState(() => readInspectorProfile());
  const [name, setName] = useState(stored.name);
  const [saved, setSaved] = useState(false);
  const license = useBoundLicense();

  function save() {
    // This form owns the display name only. The photo is written by the block above the moment it is
    // picked (photoUpdatedAt in the STORED profile), so Save writes over the profile as it is stored now —
    // never over the copy read when the page opened, which would put the photo back to what it was then
    // (Owner device test 2026-09-22 of build 93: 「头像无法保存。一点保存就消失了」).
    setStored(writeInspectorProfile({ ...currentInspectorProfile(), name }));
    setSaved(true);
  }

  return (
    <Card testId="profile-card">
      <SectionHeader title={t('settings.profile')} />
      {/* Owner 2026-09-14: the photo behind the top-right avatar is set here. */}
      <InspectorPhotoBlock />
      <p className="card-note">{t('profile.body')}</p>
      <div className="form-stack">
        <Field label={t('profile.name')} htmlFor="profile-name">
          <TextInput
            id="profile-name"
            value={name}
            onChange={(v) => {
              setName(v);
              setSaved(false);
            }}
            testId="profile-name"
          />
        </Field>
        {license ? <p className="card-note">{t('licence.displayNameNote')}</p> : null}
        {saved ? <InlineMessage kind="ok" text={t('profile.saved')} /> : null}
        <Button variant="secondary" onClick={save} testId="profile-save">
          {t('common.save')}
        </Button>
      </div>
      <div className="kv">
        <span className="kv-key">{t('profile.status')}</span>
        <span className="kv-val" data-testid="profile-status">
          {boundLicenceFields().licenseStatus === 'licensed' ? t('profile.statusLicensed') : t('profile.statusMissing')}
        </span>
      </div>
      <LicenceBinding idPrefix="profile" onBound={() => setStored(readInspectorProfile())} />
    </Card>
  );
}

function AccountPage() {
  const { t, tp } = useT();
  const session = useStore(sessionStore, (s) => s);
  const online = useOnlineStatus();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportMessage, setExportMessage] = useState<{ ok: boolean; text: string } | null>(null);

  async function changePassword() {
    if (!session.email) return;
    setBusy(true);
    try {
      const outcome = await sendPasswordReset(session.email);
      setMessage({ ok: outcome.ok, text: outcome.ok ? t('settings.changePasswordSent', { email: session.email }) : t('auth.msg.network') });
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOut() {
    setConfirmSignOut(false);
    await signOutAction();
    resetNav({ screen: 'signIn' });
  }

  // Owner decision list 2026-10-19 item 28: every report PDF (device + account) before the account goes.
  async function downloadAll() {
    setExportBusy(true);
    setExportMessage(null);
    try {
      const outcome = await exportAllReportsAction();
      const skipped = outcome.skipped > 0 ? `  ${tp('settings.downloadAllSkipped', outcome.skipped)}` : '';
      if (outcome.code === 'none') setExportMessage({ ok: outcome.skipped === 0, text: `${t('settings.downloadAllNone')}${skipped}`.trim() });
      else if (outcome.code === 'failed') setExportMessage({ ok: false, text: t('settings.downloadAllFailed', { detail: outcome.detail }) });
      else if (outcome.code === 'shared') setExportMessage({ ok: outcome.skipped === 0, text: `${tp('settings.downloadAllShared', outcome.count)}${skipped}` });
      else if (outcome.code === 'downloaded') setExportMessage({ ok: outcome.skipped === 0, text: `${tp('settings.downloadAllDownloaded', outcome.count)}${skipped}` });
    } finally {
      setExportBusy(false);
    }
  }

  async function handleDelete() {
    setConfirmDelete(false);
    setBusy(true);
    try {
      const outcome = await deleteAccountAction();
      const text = outcome.kind === 'unavailable' ? t(DELETION_KEYS.unavailable, { status: outcome.status === null ? '—' : String(outcome.status) }) : t(DELETION_KEYS[outcome.kind]);
      setMessage({ ok: outcome.kind === 'committed', text });
      // Deletion test 2026-10-18: the message above vanishes with this page, so the sign-in screen says it.
      if (outcome.kind === 'committed') resetNav({ screen: 'signIn', notice: 'accountDeleted' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card testId="account-card">
        <SectionHeader title={t('settings.account')} action={<StatusChip state={online ? 'localOnly' : 'offlineSaved'} testId="account-status-chip" />} />
        <div className="kv">
          <span className="kv-key">{t('settings.signedInAs')}</span>
          <span className="kv-val" data-testid="account-email">
            {session.email ?? '—'}
          </span>
        </div>
        {session.offlineContinuation ? <Banner tone="info" icon="wifi_off" title={t('settings.offlineContinuation')} testId="account-offline-continuation" /> : null}
        {message ? <InlineMessage kind={message.ok ? 'ok' : 'err'} text={message.text} /> : null}
        <div className="card-actions">
          <Button variant="secondary" icon="key" onClick={() => void changePassword()} disabled={busy || !session.email || !online} testId="account-change-password">
            {t('settings.changePassword')}
          </Button>
          <Button variant="secondary" icon="logout" onClick={() => setConfirmSignOut(true)} disabled={busy} testId="account-sign-out">
            {t('settings.signOut')}
          </Button>
        </div>
      </Card>
      <DevicesCard />
      <Card testId="delete-account-card">
        <SectionHeader title={t('settings.deleteAccount')} />
        <p className="card-note">{t('settings.deleteBody')}</p>
        {exportMessage ? <InlineMessage kind={exportMessage.ok ? 'ok' : 'err'} text={exportMessage.text} /> : null}
        <div className="card-actions">
          <Button variant="secondary" icon="download" onClick={() => void downloadAll()} disabled={exportBusy || busy} testId="account-download-all">
            {exportBusy ? t('settings.downloadAllWorking') : t('settings.downloadAll')}
          </Button>
          <Button variant="danger" icon="person_off" onClick={() => setConfirmDelete(true)} disabled={busy || !online} testId="account-delete">
            {t('settings.deleteAccount')}
          </Button>
        </div>
      </Card>
      {confirmSignOut ? (
        <ConfirmSheet title={t('settings.signOutTitle')} body={t('settings.signOutBody')} confirmLabel={t('settings.signOut')} onConfirm={() => void handleSignOut()} onCancel={() => setConfirmSignOut(false)} />
      ) : null}
      {confirmDelete ? (
        <ConfirmSheet title={t('settings.deleteTitle')} body={t('settings.deleteConfirmBody')} confirmLabel={t('settings.deleteConfirm')} danger onConfirm={() => void handleDelete()} onCancel={() => setConfirmDelete(false)} />
      ) : null}
    </>
  );
}

// The devices this account is signed in on (Owner decision list 2026-10-19 item 50, L-5): at most two;
// signing in on a third removes the one used longest ago.
function DevicesCard() {
  const { t } = useT();
  const devices = useStore(accountStore, (s) => s.devices);
  if (devices.length === 0) return null;
  return (
    <Card testId="devices-card">
      <SectionHeader title={t('device.listTitle')} />
      {devices.map((device) => (
        <div className="kv" key={device.deviceId} data-testid="device-row" data-this={device.thisDevice ? 'true' : 'false'}>
          <span className="kv-key">{device.label || device.platform}</span>
          <span className="kv-val">{device.thisDevice ? t('device.thisDevice') : formatDate(device.lastSeenAt)}</span>
        </div>
      ))}
      <p className="card-note">{t('device.listNote')}</p>
    </Card>
  );
}

// Sync & Storage (Round 2 §B / §AL): the ONE place that explains where the data lives — real
// status, real counts, no queue, no promise. The header never carries "local only".
function SyncPage() {
  const { t, tp } = useT();
  const online = useOnlineStatus();
  const sync = useStore(syncStatusStore, (s) => s);
  const status = storageStatusFor(online, sync);
  const inspections = useStore(inspectionStore, (s) => s.inspections.length);
  const photos = useStore(evidenceStore, (s) => s.evidence.length);
  const findings = useStore(findingStore, (s) => s.findings.length);
  const reports = useStore(reportStore, (s) => s.reports.length);
  // D-CA-8: the Owner-authorized reset of the retired flow's data is auditable on the device.
  const resetLedger = readInspectionDataResetLedger();
  const resetCount = resetLedger ? Object.values(resetLedger.counts).reduce((acc, n) => acc + n, 0) : 0;
  return (
    <>
      <Card testId="sync-status-card">
        <SectionHeader title={t('shell.syncStatus')} action={<StatusChip state={status.state} count={status.count} testId="sync-status-chip" />} />
        <p className="card-body">{t('shell.syncBody')}</p>
        <p className="card-note sync-note">{t('sync.notEnabled')}</p>
      </Card>
      <Card testId="sync-storage-card">
        <SectionHeader title={t('sync.storageTitle')} />
        <div className="stack">
          <p className="card-body">{t('sync.storageBody')}</p>
          <p className="card-body">{t('sync.offlineBody')}</p>
          <p className="card-body">{t('sync.aiBody')}</p>
          <p className="card-note">{t('sync.headerRule')}</p>
        </div>
      </Card>
      <Card testId="sync-device-card">
        <SectionHeader title={t('sync.onDevice')} />
        <div className="kv">
          <span className="kv-key">{t('nav.inspections')}</span>
          <span className="kv-val" data-testid="sync-count-inspections">
            {tp('sync.inspections', inspections)}
          </span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('pool.photos')}</span>
          <span className="kv-val" data-testid="sync-count-photos">
            {tp('common.photosCount', photos)}
          </span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('finding.findings')}</span>
          <span className="kv-val" data-testid="sync-count-findings">
            {tp('common.findingsCount', findings)}
          </span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('nav.reports')}</span>
          <span className="kv-val" data-testid="sync-count-reports">
            {tp('report.filesCount', reports)}
          </span>
        </div>
        {resetLedger ? (
          <p className="card-note" data-testid="sync-reset-ledger" data-count={resetCount}>
            {tp('sync.resetLedger', resetCount, { date: formatDate(resetLedger.resetAt) })}
          </p>
        ) : null}
      </Card>
    </>
  );
}

function PhotosPage() {
  const { t, tp } = useT();
  const photos = useStore(evidenceStore, (s) => s.evidence);
  const saved = photos.filter((p) => p.albumSaveState === 'saved').length;
  const imported = photos.filter((p) => p.source === 'photoLibrary').length;
  return (
    <>
      <Card testId="photos-rules-card">
        <SectionHeader title={t('settings.photos')} />
        <ul className="subtle-list">
          {webVersionUi() ? (
            // The web version (2026-10-20): photos come from the computer's files; there is no album.
            <>
              <li>{t('settings.photosRuleAlbum.web')}</li>
              <li>{t('settings.photosRuleDelete.web')}</li>
            </>
          ) : (
            <>
              <li>{t('settings.photosRuleAlbum')}</li>
              <li>{t('settings.photosRuleImport')}</li>
              <li>{t('settings.photosRuleDelete')}</li>
            </>
          )}
          <li>{t('settings.photosRuleNotes')}</li>
        </ul>
      </Card>
      <Card testId="photos-counts-card">
        <SectionHeader title={t('sync.onDevice')} />
        <div className="kv">
          <span className="kv-key">{t('pool.photos')}</span>
          <span className="kv-val">{tp('common.photosCount', photos.length)}</span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('photo.albumStateSaved')}</span>
          <span className="kv-val">{tp('common.photosCount', saved)}</span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('photo.imported')}</span>
          <span className="kv-val">{tp('common.photosCount', imported)}</span>
        </div>
      </Card>
    </>
  );
}

// Plan & Subscription lives in ./PlanPage.tsx (purchases, allowance, restore, subscription terms,
// Apple's offer-code redemption — Owner decisions 2026-09-11).

function HelpPage() {
  const { t } = useT();
  const steps: MessageKey[] = ['settings.helpStep1', 'settings.helpStep2', 'settings.helpStep3', 'settings.helpStep4', 'settings.helpStep5', 'settings.helpStep6', 'settings.helpStep7'];
  return (
    <>
      <Card testId="help-guide-card">
        <SectionHeader title={t('settings.helpGuide')} />
        <ol className="subtle-list help-steps">
          {steps.map((key) => (
            <li key={key}>{t(key)}</li>
          ))}
        </ol>
      </Card>
      <Card testId="help-support-card">
        <SectionHeader title={t('settings.helpSupport')} />
        <p className="card-body">{t('settings.helpSupportBody')}</p>
        <p className="card-note" style={{ marginTop: 8 }}>
          {t('settings.helpBuild', { version: APP_VERSION })}
        </p>
      </Card>
    </>
  );
}

export function PrivacyPage() {
  const { t } = useT();
  // NY F1 Round 5 (Owner ruling OD-8 / approved draft §6): ONE paragraph, the one the Owner approved,
  // saying the same thing the client's agreement says — including that generating the final report
  // deletes the inspection's working data (OD-12). It replaces the five Round-2 paragraphs, one of
  // which ("this build does not upload them") stopped being true the day AI analysis shipped.
  // Plus the two app-side facts the approved paragraph does not cover and the inspector can see for
  // themselves: the camera copy in the system photo library, and where sharing a PDF sends it.
  const paragraphs: MessageKey[] = ['settings.privacyNotice', webVersionUi() ? 'settings.privacyAppFacts.web' : 'settings.privacyAppFacts'];
  // 2026-09-25: the complete, store-registered policy is the web page (zh + en). A plain anchor is enough —
  // Capacitor hands external http(s) links to the system browser on both platforms.
  return (
    <Card testId="privacy-card">
      <SectionHeader title={t('settings.privacy')} />
      <div className="stack">
        {paragraphs.map((key) => (
          <p className="card-body" key={key}>
            {t(key)}
          </p>
        ))}
        <p className="card-note">
          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" data-testid="privacy-full-policy-link">
            {t('settings.privacyFullPolicy')}
          </a>
        </p>
      </div>
    </Card>
  );
}

export function TermsPage() {
  const { t, locale } = useT();
  return (
    <>
      <Card testId="terms-app-card">
        <SectionHeader title={t('settings.termsApp')} />
        <div className="stack">
          <p className="card-body">{t('settings.termsApp1')}</p>
          <p className="card-body">{t('settings.termsApp2')}</p>
          {/* Owner decision list 2026-10-19 item 5: the product's own Terms of Service (15 sections, English governs). */}
          <p className="card-note">
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer" data-testid="terms-full-link">
              {t('settings.termsFull')}
            </a>
          </p>
        </div>
      </Card>
      <Card testId="terms-service-card">
        <SectionHeader title={t('agreement.documentTitle')} meta={t('agreement.viewSubtitle')} />
        <div className="stack terms-text">
          {scopeTextFor(locale)
            .split('\n')
            .filter((line) => line.trim().length > 0)
            .map((line, index) => (
              <p className="card-note" key={index}>
                {line}
              </p>
            ))}
          <p className="card-note">{t('agreement.clauseNote')}</p>
          {AGREEMENT_NY_MANDATORY_CLAUSES.map((clause, index) => (
            <p className="card-note terms-clause" key={index}>
              {clause}
            </p>
          ))}
        </div>
      </Card>
    </>
  );
}

function AboutPage() {
  const { t } = useT();
  const dropped = useStore(inspectionStore, (s) => s.droppedRecords);
  const upgraded = useStore(inspectionStore, (s) => s.upgradedRecords);
  return (
    <>
      <Card testId="about-card">
        <SectionHeader title={t('shell.productName')} />
        <div className="kv">
          <span className="kv-key">{t('shell.version')}</span>
          <span className="kv-val">{APP_VERSION}</span>
        </div>
        <div className="kv">
          <span className="kv-key">{BUNDLE_ID_LABEL}</span>
          <span className="kv-val">{BUNDLE_ID}</span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('settings.aboutDatabase')}</span>
          <span className="kv-val">{V2_DATABASE_NAME}</span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('settings.aboutSchema')}</span>
          <span className="kv-val">v{V2_SCHEMA_VERSION}</span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('settings.aboutNamespace')}</span>
          <span className="kv-val">{V2_NAMESPACE_PREFIX}*</span>
        </div>
      </Card>
      <Card testId="data-integrity">
        <SectionHeader title={t('settings.aboutIntegrity')} />
        <div className="kv">
          <span className="kv-key">{t('settings.aboutUpgraded')}</span>
          <span className="kv-val">{upgraded}</span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('settings.aboutDropped')}</span>
          <span className="kv-val">{dropped}</span>
        </div>
        <p className="card-note" style={{ marginTop: 8 }}>
          {t('settings.aboutIntegrityNote')}
        </p>
      </Card>
      <Card>
        <p className="card-note">{t('settings.aboutLegacy')}</p>
      </Card>
    </>
  );
}
